import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Play, RotateCcw, Plus, Check, ArrowLeft, Eye, Ear, Layers, LayoutGrid, Subtitles, Info, ThumbsUp, ThumbsDown, Heart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { tvKey } from './tvKeys';
import { popNav, popOk, LOGO_URL, tvImg } from './tvAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_LABEL = { fiction: 'Film', documentaire: 'Documentaire', series: 'Série', jeunesse: 'Jeunesse', game: 'Jeu' };
const THUMBS = [
  { id: 'dislike', label: 'Pas pour moi', Icon: ThumbsDown },
  { id: 'like', label: "J'aime", Icon: ThumbsUp },
  { id: 'love', label: "J'adore !", Icon: Heart },
];

export function TvDetail({ item, onClose }) {
  const navigate = useNavigate();
  const { activeProfile } = useAuth();
  const [cur, setCur] = useState(item);
  const [content, setContent] = useState(item);
  const [progress, setProgress] = useState(null);
  const [zone, setZone] = useState('buttons'); // back | desc | buttons
  const [btnIdx, setBtnIdx] = useState(0);
  const [inList, setInList] = useState(false);
  const [listToast, setListToast] = useState(null);
  const toastTimer = useRef(null);
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  const [descExpanded, setDescExpanded] = useState(false);
  const [filterDefs, setFilterDefs] = useState([]);
  const [rating, setRating] = useState(null);
  const [thumbIdx, setThumbIdx] = useState(1);
  // Écrans secondaires façon Netflix
  const [screen, setScreen] = useState(null); // null | episodes | credits | similar | audio
  const [epMenuIdx, setEpMenuIdx] = useState(0);
  const [epZone, setEpZone] = useState('menu'); // menu | list
  const [epIdx, setEpIdx] = useState(0);
  const [credIdx, setCredIdx] = useState(0);
  const credRefs = useRef({});
  const [credShift, setCredShift] = useState(0);
  const [trailerFull, setTrailerFull] = useState(false);
  const [simItems, setSimItems] = useState(null);
  const [simIdx, setSimIdx] = useState(0);
  const [audZone, setAudZone] = useState('menu');
  const [audMenuIdx, setAudMenuIdx] = useState(0);
  const [audIdx, setAudIdx] = useState(0);
  const [selAudio, setSelAudio] = useState(() => sessionStorage.getItem('tv_pref_audio') || '__original__');
  const [selSub, setSelSub] = useState(() => sessionStorage.getItem('tv_pref_sub') || 'Désactivés');

  const trailerRef = useRef(null);
  const trailerDone = useRef(false);
  const trailerStartRef = useRef(null);
  const [trailerOn, setTrailerOn] = useState(() => item._trailerAt != null && !!item.trailer_url);
  const stopTrailer = () => {
    const v = trailerRef.current;
    if (v) { try { v.pause(); v.muted = true; } catch { /* ignore */ } }
    trailerDone.current = true;
    setTrailerOn(false);
  };
  const playTrailer = () => {
    trailerDone.current = false;
    trailerStartRef.current = parseFloat((content || cur).trailer_trim_start) || 0.01;
    setTrailerOn(false);
    setTimeout(() => setTrailerOn(true), 60);
  };

  useEffect(() => {
    if (trailerOn || trailerDone.current || screen) return;
    if (!(content?.trailer_url || cur.trailer_url)) return;
    const t = setTimeout(() => { if (!trailerDone.current) setTrailerOn(true); }, 2000);
    return () => clearTimeout(t);
  }, [trailerOn, content, cur.trailer_url, screen]);

  useEffect(() => {
    axios.get(`${API}/filters`, { withCredentials: true }).then(r => setFilterDefs(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/watchlist`, { params: activeProfile ? { profile_id: activeProfile.id } : {}, withCredentials: true })
      .then(r => setInList((r.data || []).some(c => c.id === cur.id)))
      .catch(() => { /* ignore */ });
  }, [cur.id, activeProfile]);

  const toggleList = () => {
    const pid = activeProfile?.id || '';
    if (inList) {
      axios.delete(`${API}/watchlist/${cur.id}`, { params: { profile_id: pid }, withCredentials: true }).catch(() => {});
      setInList(false);
      setListToast('removed');
    } else {
      axios.post(`${API}/watchlist/${cur.id}`, { profile_id: pid }, { withCredentials: true }).catch(() => {});
      setInList(true);
      setListToast('added');
    }
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setListToast(null), 2200);
    try { window.dispatchEvent(new CustomEvent('tv-watchlist-changed')); } catch { /* ignore */ }
  };

  useEffect(() => {
    axios.get(`${API}/contents/${cur.id}`, { withCredentials: true }).then(r => setContent(r.data)).catch(() => {});
    axios.get(`${API}/watch-progress/${cur.id}`, { params: activeProfile ? { profile_id: activeProfile.id } : {}, withCredentials: true })
      .then(r => setProgress(r.data)).catch(() => {});
    axios.get(`${API}/ratings`, { withCredentials: true }).then(r => setRating((r.data || {})[cur.id] || null)).catch(() => {});
  }, [cur.id, activeProfile]);

  const rate = (id) => {
    const next = rating === id ? null : id;
    setRating(next);
    axios.post(`${API}/ratings`, { content_id: cur.id, rating: next }, { withCredentials: true }).catch(() => {});
  };

  // Titres similaires : même catégorie / tags partagés
  useEffect(() => {
    if (screen !== 'similar' || simItems) return;
    axios.get(`${API}/contents`, { params: { slim: 1 }, withCredentials: true }).then(r => {
      const all = (r.data || []).filter(c => c.id !== cur.id && c.status !== 'draft');
      const cats = new Set([content?.category, ...(content?.categories || [])].filter(Boolean));
      const tags = new Set(content?.tags || []);
      const score = c => {
        let s = 0;
        if (cats.has(c.category)) s += 3;
        (c.categories || []).forEach(x => { if (cats.has(x)) s += 2; });
        (c.tags || []).forEach(t => { if (tags.has(t)) s += 1; });
        if (c.type === content?.type) s += 1;
        return s;
      };
      setSimItems(all.map(c => [score(c), c]).sort((a, b) => b[0] - a[0]).slice(0, 12).map(x => x[1]));
    }).catch(() => setSimItems([]));
  }, [screen, simItems, content, cur.id]);

  const openContent = (c) => {
    trailerDone.current = false;
    trailerStartRef.current = null;
    setTrailerOn(false);
    setScreen(null); setSimItems(null); setSimIdx(0);
    setProgress(null); setInList(false);
    setZone('buttons'); setBtnIdx(0); setEpMenuIdx(0); setEpIdx(0); setEpZone('menu');
    setContent(c); setCur(c);
  };

  const canResume = progress && progress.progress > 5 && progress.progress < 95;
  const episodes = content?.type === 'series' ? (content.episodes || []) : [];
  const sortedEps = [...episodes].sort((a, b) => (a.season || 1) - (b.season || 1) || (a.episode || 0) - (b.episode || 0));
  const watchedIds = progress?.watched_episode_ids || [];
  const nextUnseenEp = sortedEps.find(e => !watchedIds.includes(e.id)) || sortedEps[0] || null;
  const longDesc = (content?.description || '').length > 250;
  const playLabel = content?.type === 'series' && episodes.length
    ? (nextUnseenEp && watchedIds.length > 0 ? `Lire S${nextUnseenEp.season || 1} E${nextUnseenEp.episode || 1}` : 'Regarder')
    : 'Lecture';

  const buttons = [];
  if (content?.type === 'game') {
    buttons.push({ label: 'Jouer', action: 'play', Icon: Play });
  } else if (canResume) {
    buttons.push({ label: `Reprendre${fmtTime(progress.current_time)}`, action: 'play', Icon: Play });
    buttons.push({ label: 'Lire depuis le début', action: 'restart', Icon: RotateCcw });
  } else {
    buttons.push({ label: playLabel, action: 'play', Icon: Play });
  }
  if (content?.trailer_url) buttons.push({ label: 'Regarder la bande-annonce', action: 'trailer', Icon: Play, outline: true });
  if (episodes.length > 0) buttons.push({ label: 'Épisodes et autres', action: 'episodes', Icon: Layers });
  buttons.push({ label: 'Titres similaires', action: 'similar', Icon: LayoutGrid });
  if (content?.type !== 'game') buttons.push({ label: 'Audio et sous-titres', action: 'audio', Icon: Subtitles });
  buttons.push({ label: inList ? 'Retirer de Ma liste' : 'Ajouter à Ma liste', action: 'list', Icon: inList ? Check : Plus });
  buttons.push({ label: 'Crédits et autres infos', action: 'credits', Icon: Info });

  const isOriginal = content?.category === 'originals' || (content?.categories || []).includes('originals');
  const subtitleLang = content?.burned_subtitles_lang
    || ((content?.language && content.language !== 'Français' && (content?.subtitles || []).some(s => (s.language || '').toLowerCase().startsWith('fran'))) ? 'Français' : '');
  const activeFilterIds = [...new Set((content?.content_filters || []).map(f => f.filter_id || f.type))];

  // Écran épisodes : menu saisons + bandes-annonces
  const seasonNums = [...new Set(sortedEps.map(e => e.season || 1))].sort((a, b) => a - b);
  const epMenu = [
    ...seasonNums.map(n => ({ type: 'season', n, label: seasonNums.length > 1 ? `Saison ${n}` : 'Épisodes', count: sortedEps.filter(e => (e.season || 1) === n).length })),
    ...(content?.trailer_url ? [{ type: 'trailers', label: 'Bandes-annonces', count: 1 }] : []),
  ];
  const epActive = epMenu[epMenuIdx] || epMenu[0];
  const epList = epActive?.type === 'season' ? sortedEps.filter(e => (e.season || 1) === epActive.n) : [{ trailer: true }];

  // Écran crédits : sections dynamiques (+ filtres du contenu)
  const credSections = [];
  if (content?.director) credSections.push({ name: 'Réalisation', lines: content.director.split(',').map(s => s.trim()) });
  if (content?.actors?.length) {
    const cast = content.actors.filter(a => a && a.trim());
    if (cast.length) credSections.push({ name: 'Distribution', lines: cast });
  }
  const genres = [...new Set([TYPE_LABEL[content?.type], content?.language, ...(content?.tags || [])].filter(Boolean))];
  if (genres.length) credSections.push({ name: 'Genres', lines: genres });
  if (content?.age_rating) {
    const ageNum = String(content.age_rating).replace(/\D/g, '');
    credSections.push({ name: "Catégorie d'âge", badge: content.age_rating, lines: [ageNum ? `Recommandé pour les ${ageNum} ans et plus` : 'Programme tout public, à regarder en famille'] });
  }
  if (activeFilterIds.length) credSections.push({ name: content?.type === 'series' ? 'Filtres de cette série' : 'Filtres de ce film', filters: true });

  // Écran audio & sous-titres
  const audioOpts = [{ id: '__original__', label: `${content?.language || 'Version'} originale` },
    ...((content?.audio_tracks || []).map(t => ({ id: t.language, label: t.language })))];
  const subOpts = [{ id: 'Désactivés', label: 'Désactivés' },
    ...((content?.subtitles || []).map(s => ({ id: s.language, label: s.language })))];
  const audMenu = [{ label: 'Audio', opts: audioOpts }, { label: 'Sous-titres', opts: subOpts }];
  const audOpts = audMenu[audMenuIdx]?.opts || [];

  const startWatch = (epId) => {
    stopTrailer();
    try { sessionStorage.setItem('tv_resume', JSON.stringify({ id: cur.id, title: content?.title })); } catch { /* ignore */ }
    navigate(`/watch/${cur.id}${epId ? `?episode=${epId}` : ''}`);
  };

  useEffect(() => {
    if (screen !== 'credits') { setCredShift(0); return; }
    const el = credRefs.current[credIdx];
    if (el) setCredShift(el.offsetTop);
  }, [credIdx, screen]);

  const doAction = (b) => {
    if (b.action === 'trailer') { stopTrailer(); setTrailerFull(true); return; }
    if (b.action === 'list') { toggleList(); return; }
    if (['episodes', 'similar', 'audio', 'credits'].includes(b.action)) {
      stopTrailer();
      if (b.action === 'episodes') { setEpMenuIdx(0); setEpIdx(0); setEpZone('menu'); }
      if (b.action === 'similar') { setSimIdx(0); }
      if (b.action === 'audio') { setAudZone('menu'); setAudMenuIdx(0); setAudIdx(0); }
      if (b.action === 'credits') { setCredIdx(0); }
      setScreen(b.action);
      return;
    }
    stopTrailer();
    if (content?.type === 'game') {
      if (content?.game_url || content?.video_url) {
        try { sessionStorage.setItem('tv_resume', JSON.stringify({ id: cur.id, title: content?.title })); } catch { /* ignore */ }
        navigate(`/game/${cur.id}`);
      }
    } else if (b.action === 'restart') {
      try { sessionStorage.setItem('tv_resume', JSON.stringify({ id: cur.id, title: content?.title })); } catch { /* ignore */ }
      axios.post(`${API}/watch-history`, { content_id: cur.id, profile_id: activeProfile?.id, progress: 0, current_time: 0, total_duration: progress?.total_duration || 0 }, { withCredentials: true }).finally(() => navigate(`/watch/${cur.id}`));
    } else {
      const epId = !canResume && content?.type === 'series' && nextUnseenEp ? nextUnseenEp.id : '';
      startWatch(epId);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      const k = tvKey(e);
      if (!k) return;
      e.preventDefault();
      e.stopPropagation();
      if (trailerFull) { if (k === 'back' || k === 'ok') { popOk(); setTrailerFull(false); } return; }
      if (descExpanded) { if (k === 'back' || k === 'ok') { popOk(); setDescExpanded(false); } return; }

      if (screen === 'episodes') {
        if (k === 'back') { popOk(); setScreen(null); return; }
        if (epZone === 'menu') {
          if (k === 'up') { popNav(); setEpMenuIdx(i => Math.max(0, i - 1)); setEpIdx(0); }
          else if (k === 'down') { popNav(); setEpMenuIdx(i => Math.min(epMenu.length - 1, i + 1)); setEpIdx(0); }
          else if (k === 'right' || k === 'ok') { popNav(); setEpZone('list'); setEpIdx(0); }
        } else {
          if (k === 'left') { popNav(); setEpZone('menu'); }
          else if (k === 'up') { popNav(); setEpIdx(i => Math.max(0, i - 1)); }
          else if (k === 'down') { popNav(); setEpIdx(i => Math.min(epList.length - 1, i + 1)); }
          else if (k === 'ok') {
            popOk();
            if (epList[epIdx]?.trailer) { setScreen(null); stopTrailer(); setTrailerFull(true); }
            else startWatch(epList[epIdx]?.id || '');
          }
        }
        return;
      }
      if (screen === 'credits') {
        if (k === 'back' || k === 'ok') { popOk(); setScreen(null); return; }
        if (k === 'up') { popNav(); setCredIdx(i => Math.max(0, i - 1)); }
        else if (k === 'down') { popNav(); setCredIdx(i => Math.min(credSections.length - 1, i + 1)); }
        return;
      }
      if (screen === 'similar') {
        if (k === 'back') { popOk(); setScreen(null); setSimItems(null); return; }
        const n = (simItems || []).length;
        if (!n) return;
        if (k === 'left') { popNav(); setSimIdx(i => Math.max(0, i - 1)); }
        else if (k === 'right') { popNav(); setSimIdx(i => Math.min(n - 1, i + 1)); }
        else if (k === 'up') { popNav(); setSimIdx(i => Math.max(0, i - 4)); }
        else if (k === 'down') { popNav(); setSimIdx(i => Math.min(n - 1, i + 4)); }
        else if (k === 'ok') { popOk(); openContent(simItems[simIdx]); }
        return;
      }
      if (screen === 'audio') {
        if (k === 'back') { popOk(); setScreen(null); return; }
        if (audZone === 'menu') {
          if (k === 'up') { popNav(); setAudMenuIdx(i => Math.max(0, i - 1)); setAudIdx(0); }
          else if (k === 'down') { popNav(); setAudMenuIdx(i => Math.min(audMenu.length - 1, i + 1)); setAudIdx(0); }
          else if (k === 'right' || k === 'ok') { popNav(); setAudZone('list'); }
        } else {
          if (k === 'left') { popNav(); setAudZone('menu'); }
          else if (k === 'up') { popNav(); setAudIdx(i => Math.max(0, i - 1)); }
          else if (k === 'down') { popNav(); setAudIdx(i => Math.min(audOpts.length - 1, i + 1)); }
          else if (k === 'ok') {
            popOk();
            const o = audOpts[audIdx];
            if (!o) return;
            if (audMenuIdx === 0) { setSelAudio(o.id); try { sessionStorage.setItem('tv_pref_audio', o.id); } catch { /* ignore */ } }
            else { setSelSub(o.id); try { sessionStorage.setItem('tv_pref_sub', o.id); } catch { /* ignore */ } }
          }
        }
        return;
      }

      if (k === 'back') { onClose(); return; }
      if (zone === 'back') {
        if (k === 'down') { popNav(); setZone(longDesc ? 'desc' : 'thumbs'); }
        else if (k === 'ok') { popOk(); onClose(); }
      } else if (zone === 'desc') {
        if (k === 'up') { popNav(); setZone('back'); }
        else if (k === 'down') { popNav(); setZone('thumbs'); }
        else if (k === 'ok') { popOk(); setDescExpanded(true); }
      } else if (zone === 'thumbs') {
        if (k === 'left') { popNav(); setThumbIdx(i => Math.max(0, i - 1)); }
        else if (k === 'right') { popNav(); setThumbIdx(i => Math.min(THUMBS.length - 1, i + 1)); }
        else if (k === 'up') { popNav(); setZone(longDesc ? 'desc' : 'back'); }
        else if (k === 'down') { popNav(); setZone('buttons'); setBtnIdx(0); }
        else if (k === 'ok') { popOk(); rate(THUMBS[thumbIdx].id); }
      } else {
        if (k === 'up') {
          popNav();
          if (btnIdx > 0) setBtnIdx(i => i - 1);
          else setZone('thumbs');
        }
        else if (k === 'down') { if (btnIdx < buttons.length - 1) { popNav(); setBtnIdx(i => i + 1); } }
        else if (k === 'ok') { popOk(); doAction(buttons[btnIdx]); }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  // En-tête commun des écrans secondaires
  const subHeader = (
    <div style={{ position: 'absolute', top: '5vh', left: '4vw', maxWidth: '30vw' }}>
      {isOriginal && <img src={LOGO_URL} alt="Maya+" style={{ height: '1.2vw', marginBottom: '0.6vw', objectFit: 'contain', objectPosition: 'left', display: 'block' }} />}
      {content?.logo_url
        ? <img src={content.logo_url} alt={content?.title} style={{ maxHeight: '5vw', maxWidth: '22vw', objectFit: 'contain', objectPosition: 'left', marginBottom: '0.8vw' }} />
        : <h2 style={{ fontSize: '2.2vw', fontWeight: 800, lineHeight: 1.05, marginBottom: '0.8vw' }}>{content?.title}</h2>}
      <p style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.6)' }}>
        {content?.year || ''} · {TYPE_LABEL[content?.type] || ''}
        {content?.age_rating && <span style={{ border: '1px solid rgba(255,255,255,0.35)', padding: '0.08vw 0.5vw', borderRadius: '0.25vw', marginLeft: '0.8vw', fontSize: '0.85vw' }}>{content.age_rating}</span>}
      </p>
    </div>
  );
  const subScreenStyle = { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 25, background: '#0b0612' };
  const menuItemStyle = (fc, sel) => ({
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8vw 1.6vw', marginBottom: '0.6vw',
    borderRadius: '2.5vw', cursor: 'pointer', fontSize: '1.2vw', fontWeight: 700,
    background: fc ? '#fff' : (sel ? 'rgba(255,255,255,0.14)' : 'transparent'),
    color: fc ? '#0b0612' : (sel ? '#fff' : 'rgba(255,255,255,0.75)'),
  });

  return (
    <div className="tv-fade-in" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: '#0b0612', zIndex: 20, overflow: 'hidden' }} data-testid="tv-detail">
      <div className={`tv-btn ${zone === 'back' ? 'tv-focused' : ''}`} onClick={onClose}
        style={{ position: 'absolute', top: '1.5vw', left: '2vw', zIndex: 3, display: 'flex', alignItems: 'center',
          padding: '0.45vw 1.2vw', borderRadius: '2vw', background: zone === 'back' ? '#E9641A' : 'rgba(255,255,255,0.12)', cursor: 'pointer', fontSize: '1vw', fontWeight: 700 }}
        data-testid="tv-back-btn">
        <ArrowLeft style={{ width: '1vw', height: '1vw', marginRight: '0.5vw' }} />
        Retour
      </div>
      {/* Logo Maya+ (originals) à côté du bouton Retour */}
      {isOriginal && !screen && (
        <img src={LOGO_URL} alt="Maya+" style={{ position: 'absolute', top: '1.85vw', left: '11.5vw', height: '1.5vw', zIndex: 3, objectFit: 'contain' }} data-testid="tv-detail-original-logo" />
      )}
      {/* Visuel à droite */}
      <img src={tvImg(content?.banner_url || content?.thumbnail_url, 1280)} alt=""
        style={{ position: 'absolute', top: 0, right: 0, width: '64%', height: '100%', objectFit: 'cover' }} />
      {trailerOn && (content?.trailer_url || cur.trailer_url) && (
        <video
          ref={el => {
            trailerRef.current = el;
            if (el && !el.dataset.boot) {
              el.dataset.boot = '1';
              el.muted = false;
              el.play().catch(() => {
                el.muted = true;
                el.play().catch(() => setTrailerOn(false));
              });
            }
          }}
          src={content?.trailer_url || cur.trailer_url} playsInline
          onLoadedMetadata={e => { const ts = parseFloat((content || cur).trailer_trim_start) || 0; const at = trailerStartRef.current != null ? trailerStartRef.current : (cur._trailerAt || ts); if (at) { try { e.target.currentTime = at; } catch { /* ignore */ } } }}
          onTimeUpdate={e => { const te = parseFloat((content || cur).trailer_trim_end) || 0; if (te > 0 && e.target.currentTime >= te) { trailerDone.current = true; setTrailerOn(false); } }}
          onEnded={() => { trailerDone.current = true; setTrailerOn(false); }} onError={() => { trailerDone.current = true; setTrailerOn(false); }}
          style={{ position: 'absolute', top: 0, right: 0, width: '64%', height: '100%', objectFit: 'cover', animation: 'tvpFadeIn 1.2s ease' }}
          data-testid="tv-detail-trailer" />
      )}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background:
        'linear-gradient(90deg, #0b0612 34%, rgba(11,6,18,0.85) 45%, rgba(11,6,18,0.2) 62%, rgba(11,6,18,0.05) 100%), linear-gradient(0deg, #0b0612 6%, transparent 45%)' }} />
      {trailerOn && isOriginal && (
        <img src={LOGO_URL} alt="Maya+" style={{ position: 'absolute', top: '1vw', right: '1.2vw', height: '1.4vw', width: 'auto', zIndex: 3,
          filter: 'drop-shadow(0 0.1vw 0.3vw rgba(0,0,0,0.9))', pointerEvents: 'none' }} data-testid="tv-trailer-original-logo" />
      )}

      {/* Colonne gauche façon Netflix */}
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '3.5vw 0 2vw 4vw', maxWidth: '46vw' }}>
        {content?.logo_url
          ? <img src={content.logo_url} alt={content?.title} style={{ maxHeight: '7.5vw', maxWidth: '26vw', objectFit: 'contain', objectPosition: 'left', marginBottom: '0.9vw' }} />
          : <h1 style={{ fontSize: '2.8vw', fontWeight: 800, lineHeight: 1.05, marginBottom: '0.9vw' }} data-testid="tv-detail-title">{content?.title}</h1>}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.7vw', fontSize: '1.25vw', color: 'rgba(255,255,255,0.85)', flexWrap: 'wrap' }}>
          {content?.year && <span style={{ marginRight: '1vw' }}>{content.year}</span>}
          <span style={{ fontWeight: 700, marginRight: '1vw' }}>{TYPE_LABEL[content?.type] || ''}</span>
          {content?.duration && <span style={{ marginRight: '1vw' }}>{content.duration}</span>}
          {episodes.length > 0 && <span style={{ marginRight: '1vw' }}>{episodes.length} épisode{episodes.length > 1 ? 's' : ''}</span>}
          {content?.age_rating && <span style={{ border: '1px solid rgba(255,255,255,0.4)', padding: '0.08vw 0.55vw', borderRadius: '0.25vw', fontSize: '0.9vw' }}>{content.age_rating}</span>}
          {subtitleLang && <span style={{ border: '1px solid rgba(255,255,255,0.4)', padding: '0.08vw 0.55vw', borderRadius: '0.25vw', fontSize: '0.9vw', marginLeft: '0.6vw' }}>ST {subtitleLang.toLowerCase()}</span>}
        </div>
        {activeFilterIds.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.9vw', flexWrap: 'wrap' }} data-testid="tv-detail-filters">
            <span style={{ padding: '0.3vw 1vw', marginRight: '0.8vw', marginBottom: '0.5vw', background: 'rgba(233,100,26,0.15)', border: '1px solid rgba(233,100,26,0.45)', borderRadius: '0.4vw', fontSize: '1vw', fontWeight: 700, color: '#E9641A' }}>
              {content.type === 'series' ? 'Cette série contient des filtres' : 'Ce film contient des filtres'}
            </span>
            {activeFilterIds.map(fId => {
              const def = filterDefs.find(af => af.id === fId);
              if (!def) return null;
              const FIcon = def.action === 'mute' ? Ear : Eye;
              return (
                <span key={fId} style={{ display: 'inline-flex', alignItems: 'center', padding: '0.3vw 0.9vw', marginRight: '0.8vw', marginBottom: '0.5vw',
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '0.4vw', fontSize: '0.95vw', color: 'rgba(255,255,255,0.7)' }}>
                  <FIcon style={{ width: '0.95vw', height: '0.95vw', marginRight: '0.5vw' }} />
                  {def.name}
                </span>
              );
            })}
          </div>
        )}
        <p style={{ fontSize: '1.35vw', color: 'rgba(255,255,255,0.78)', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: '0.6vw', flex: 'none' }}
          data-testid="tv-detail-description">
          {content?.description}
        </p>
        {(content?.actors || []).filter(a => a && a.trim()).length > 0 && (
          <p style={{ fontSize: '1.15vw', color: 'rgba(255,255,255,0.55)', marginBottom: '0.3vw' }} data-testid="tv-detail-cast">
            Distribution : <span style={{ color: 'rgba(255,255,255,0.8)' }}>{content.actors.filter(a => a && a.trim()).slice(0, 4).join(', ')}</span>
          </p>
        )}
        {content?.director && (
          <p style={{ fontSize: '1.15vw', color: 'rgba(255,255,255,0.55)', marginBottom: '0.45vw' }} data-testid="tv-detail-director">
            Réalisation : <span style={{ color: 'rgba(255,255,255,0.8)' }}>{content.director}</span>
          </p>
        )}
        {longDesc && (
          <div className={`tv-btn ${zone === 'desc' ? 'tv-focused' : ''}`} onClick={() => setDescExpanded(true)}
            style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start', padding: '0.3vw 1.1vw', marginBottom: '0.6vw',
              fontSize: '1.1vw', fontWeight: 700, borderRadius: '2vw', cursor: 'pointer',
              background: zone === 'desc' ? '#fff' : 'rgba(255,255,255,0.1)', color: zone === 'desc' ? '#000' : 'rgba(255,255,255,0.75)' }}
            data-testid="tv-desc-more-btn">
            Plus
          </div>
        )}
        {/* Notes pouces façon Netflix */}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.5vw', marginBottom: '0.5vw' }} data-testid="tv-thumbs-row">
          {THUMBS.map((t, i) => {
            const fc = zone === 'thumbs' && i === thumbIdx;
            const sel = rating === t.id;
            const TIcon = t.Icon;
            return (
              <div key={t.id} onClick={() => rate(t.id)} data-testid={`tv-thumb-${t.id}`}
                style={{ width: '3vw', height: '3vw', borderRadius: '50%', marginRight: '1.1vw', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  background: fc ? '#fff' : (sel ? 'rgba(233,100,26,0.2)' : 'rgba(255,255,255,0.08)'),
                  border: sel ? '0.15vw solid #E9641A' : '0.15vw solid transparent' }}>
                <TIcon style={{ width: '1.4vw', height: '1.4vw', color: fc ? '#0b0612' : (sel ? '#E9641A' : 'rgba(255,255,255,0.85)'),
                  fill: sel && t.id === 'love' ? '#E9641A' : 'none' }} />
              </div>
            );
          })}
          <span style={{ fontSize: '1vw', fontWeight: 700, color: zone === 'thumbs' ? '#fff' : 'rgba(255,255,255,0.5)' }} data-testid="tv-thumb-label">
            {zone === 'thumbs' ? THUMBS[thumbIdx].label : (rating ? `Votre avis : ${THUMBS.find(t => t.id === rating)?.label}` : '')}
          </span>
        </div>
        {/* Boutons verticaux façon Netflix, lignes bien espacées */}
        <div style={{ marginTop: '0.6vw', maxHeight: '46vh', overflow: 'hidden' }}>
          <div style={{ transform: `translateY(${zone === 'buttons' && btnIdx > 3 ? -(btnIdx - 3) * 4.3 : 0}vw)`, transition: 'transform 0.2s ease' }}>
            {buttons.map((b, i) => {
              const fc = zone === 'buttons' && i === btnIdx;
              const BtnIcon = b.Icon;
              return (
                <div key={b.action + i} className="tv-btn" onClick={() => doAction(b)}
                  style={{ display: 'flex', alignItems: 'center', padding: '0.8vw 1.9vw', marginBottom: '0.9vw', borderRadius: '2.8vw',
                    width: 'fit-content', minWidth: '26vw', fontSize: '1.5vw', fontWeight: 700, cursor: 'pointer',
                    background: fc ? '#fff' : 'transparent', color: fc ? '#0b0612' : 'rgba(255,255,255,0.92)' }}
                  data-testid={`tv-detail-btn-${b.action}`}>
                  <BtnIcon style={{ width: '1.7vw', height: '1.7vw', marginRight: '1.2vw', flex: 'none', fill: (b.action === 'play' && !b.outline) ? 'currentColor' : 'none' }} />
                  {b.label}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══════ Écran Épisodes et autres ═══════ */}
      {screen === 'episodes' && (
        <div style={subScreenStyle} data-testid="tv-episodes-screen">
          {subHeader}
          <div style={{ position: 'absolute', top: '22vh', left: '4vw', width: '28vw' }}>
            {epMenu.map((m, i) => {
              const fc = epZone === 'menu' && i === epMenuIdx;
              return (
                <div key={m.label + i} style={menuItemStyle(fc, i === epMenuIdx)} data-testid={`tv-epmenu-${i}`}
                  onClick={() => { setEpMenuIdx(i); setEpZone('list'); setEpIdx(0); }}>
                  <span>{m.label}</span>
                  <span style={{ fontSize: '0.95vw', fontWeight: 600, opacity: 0.65, marginLeft: '2vw' }}>
                    {m.type === 'season' ? `${m.count} épisode${m.count > 1 ? 's' : ''}` : `${m.count} vidéo${m.count > 1 ? 's' : ''}`}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ position: 'absolute', top: '10vh', left: '38vw', right: '4vw', bottom: 0, overflow: 'hidden' }}>
            <div style={{ transform: `translateY(${-epIdx * 8.4}vw)`, transition: 'transform 0.25s ease' }}>
              {epList.map((ep, i) => {
                const fc = epZone === 'list' && i === epIdx;
                if (ep.trailer) {
                  return (
                    <div key="trailer" style={{ display: 'flex', alignItems: 'flex-start', padding: '0.7vw', marginBottom: '1vw', borderRadius: '0.6vw',
                      background: fc ? 'rgba(255,255,255,0.1)' : 'transparent' }} data-testid="tv-episode-trailer"
                      onClick={() => { setScreen(null); stopTrailer(); setTrailerFull(true); }}>
                      <img src={tvImg(content?.thumbnail_url, 400)} alt="" style={{ width: '13vw', height: '7.3vw', objectFit: 'cover', borderRadius: '0.5vw', flex: 'none', marginRight: '2vw',
                        border: fc ? '0.2vw solid #fff' : '0.2vw solid transparent' }} />
                      <div>
                        <p style={{ fontWeight: 800, fontSize: '1.25vw', marginBottom: '0.5vw' }}>Bande-annonce</p>
                        <p style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.55)' }}>{content?.title}</p>
                      </div>
                    </div>
                  );
                }
                const isCur = ep.id === progress?.episode_id;
                const epInProgress = isCur && (progress?.progress || 0) > 0 && (progress?.progress || 0) < 95;
                const isSeen = watchedIds.includes(ep.id) && !epInProgress;
                return (
                  <div key={ep.id || i} style={{ display: 'flex', alignItems: 'flex-start', padding: '0.7vw', marginBottom: '1vw', borderRadius: '0.6vw',
                    background: fc ? 'rgba(255,255,255,0.1)' : 'transparent' }}
                    data-testid={`tv-episode-${i}`} onClick={() => startWatch(ep.id)}>
                    <div style={{ position: 'relative', flex: 'none', marginRight: '2vw' }}>
                      <img src={tvImg(ep.thumbnail_url || content?.thumbnail_url, 400)} alt=""
                        style={{ width: '13vw', height: '7.3vw', objectFit: 'cover', borderRadius: '0.5vw', display: 'block', opacity: isSeen ? 0.55 : 1,
                          border: fc ? '0.2vw solid #fff' : '0.2vw solid transparent' }} />
                      <span style={{ position: 'absolute', bottom: '0.5vw', left: '0.5vw', fontSize: '0.95vw', fontWeight: 800, textShadow: '0 0.1vw 0.3vw #000' }}>
                        S{ep.season || 1} : E{ep.episode || i + 1}
                      </span>
                      {isSeen && (
                        <span data-testid={`tv-ep-seen-${ep.id}`} style={{ position: 'absolute', top: '0.4vw', right: '0.4vw', width: '1.4vw', height: '1.4vw', borderRadius: '50%',
                          background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0.1vw 0.3vw rgba(0,0,0,0.6)' }}>
                          <Check style={{ width: '0.85vw', height: '0.85vw', color: '#fff' }} strokeWidth={3.5} />
                        </span>
                      )}
                      {epInProgress && (
                        <div data-testid={`tv-ep-progress-${ep.id}`} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '0.3vw', background: 'rgba(255,255,255,0.3)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${progress.progress}%`, background: '#E9641A' }} />
                        </div>
                      )}
                    </div>
                    <div style={{ paddingTop: '0.3vw' }}>
                      <p style={{ fontWeight: 800, fontSize: '1.2vw', marginBottom: '0.5vw' }}>
                        Épisode {ep.episode || i + 1}{ep.title ? ` · ${ep.title}` : ''}
                        {isSeen && <span style={{ marginLeft: '0.8vw', fontSize: '0.8vw', fontWeight: 800, color: '#10b981', letterSpacing: '0.05em' }}>VU</span>}
                        {epInProgress && <span style={{ marginLeft: '0.8vw', fontSize: '0.8vw', fontWeight: 800, color: '#FE975E', letterSpacing: '0.05em' }}>EN COURS</span>}
                      </p>
                      <p style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: '38vw' }}>{ep.description}</p>
                      {ep.duration && <p style={{ fontSize: '0.9vw', color: 'rgba(255,255,255,0.4)', marginTop: '0.4vw' }}>({ep.duration})</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ Écran Crédits et autres infos ═══════ */}
      {screen === 'credits' && (
        <div style={subScreenStyle} data-testid="tv-credits-screen">
          {subHeader}
          <div style={{ position: 'absolute', top: '22vh', left: '4vw', width: '26vw' }}>
            {credSections.map((s, i) => (
              <div key={s.name} style={menuItemStyle(i === credIdx, false)} data-testid={`tv-credmenu-${i}`} onClick={() => setCredIdx(i)}>
                <span>{s.name}</span>
              </div>
            ))}
          </div>
          <div style={{ position: 'absolute', top: '18vh', left: '38vw', right: '4vw', bottom: '4vh', overflow: 'hidden' }}>
            <div style={{ transform: `translateY(${-credShift}px)`, transition: 'transform 0.3s ease' }} data-testid="tv-credits-content">
              {credSections.map((s, si) => (
                <div key={s.name} ref={el => { credRefs.current[si] = el; }} style={{ marginBottom: '1.8vw' }}>
                <p style={{ fontSize: '1.15vw', fontWeight: 800, marginBottom: '0.6vw' }}>{s.name}</p>
                {s.badge && <span style={{ display: 'inline-block', border: '1px solid rgba(255,255,255,0.4)', padding: '0.1vw 0.6vw', borderRadius: '0.25vw', fontSize: '0.95vw', marginBottom: '0.5vw' }}>{s.badge}</span>}
                {s.filters
                  ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {activeFilterIds.map(fId => {
                        const def = filterDefs.find(af => af.id === fId);
                        if (!def) return null;
                        const FIcon = def.action === 'mute' ? Ear : Eye;
                        return (
                          <span key={fId} style={{ display: 'inline-flex', alignItems: 'center', padding: '0.3vw 0.9vw', marginRight: '0.8vw', marginBottom: '0.6vw',
                            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '0.4vw', fontSize: '1vw', color: 'rgba(255,255,255,0.75)' }}>
                            <FIcon style={{ width: '1vw', height: '1vw', marginRight: '0.5vw' }} />
                            {def.name}
                          </span>
                        );
                      })}
                    </div>
                  )
                  : (s.lines || []).map(l => (
                    <p key={l} style={{ fontSize: '1.05vw', color: 'rgba(255,255,255,0.75)', marginBottom: '0.3vw' }}>{l}</p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ Bande-annonce plein écran ═══════ */}
      {trailerFull && (content?.trailer_url || cur.trailer_url) && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 60, background: '#000' }} data-testid="tv-trailer-fullscreen"
          onClick={() => setTrailerFull(false)}>
          <video autoPlay playsInline src={content?.trailer_url || cur.trailer_url}
            ref={el => { if (el && !el.dataset.boot) { el.dataset.boot = '1'; el.muted = false; el.play().catch(() => { el.muted = true; el.play().catch(() => {}); }); } }}
            onLoadedMetadata={e => { const ts = parseFloat((content || cur).trailer_trim_start) || 0; if (ts) { try { e.target.currentTime = ts; } catch { /* ignore */ } } }}
            onTimeUpdate={e => { const te = parseFloat((content || cur).trailer_trim_end) || 0; if (te > 0 && e.target.currentTime >= te) setTrailerFull(false); }}
            onEnded={() => setTrailerFull(false)} onError={() => setTrailerFull(false)}
            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
          <p style={{ position: 'absolute', bottom: '3vh', left: 0, right: 0, textAlign: 'center', fontSize: '0.95vw', color: 'rgba(255,255,255,0.45)' }}>Retour ou OK pour quitter</p>
        </div>
      )}

      {/* ═══════ Écran Titres similaires ═══════ */}
      {screen === 'similar' && (
        <div style={subScreenStyle} data-testid="tv-similar-screen">
          {subHeader}
          <div style={{ position: 'absolute', top: '22vh', left: '4vw', right: '4vw', bottom: 0, overflow: 'hidden' }}>
            <p style={{ fontSize: '1.3vw', fontWeight: 800, marginBottom: '1.2vw' }}>Titres similaires</p>
            {!simItems && <p style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.5)' }}>Chargement…</p>}
            {simItems && simItems.length === 0 && <p style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.5)' }}>Aucun titre similaire pour le moment.</p>}
            <div style={{ transform: `translateY(${-Math.max(0, Math.floor(simIdx / 4) - 1) * 10.2}vw)`, transition: 'transform 0.25s ease', display: 'flex', flexWrap: 'wrap' }}>
              {(simItems || []).map((c, i) => (
                <div key={c.id} style={{ width: '21vw', marginRight: '1.6vw', marginBottom: '1.6vw', cursor: 'pointer' }}
                  data-testid={`tv-similar-${i}`} onClick={() => openContent(c)}>
                  <img src={tvImg(c.thumbnail_url || c.banner_url, 400)} alt={c.title}
                    style={{ width: '21vw', height: '11.8vw', objectFit: 'cover', borderRadius: '0.5vw', display: 'block',
                      border: i === simIdx ? '0.25vw solid #fff' : '0.25vw solid transparent',
                      transform: i === simIdx ? 'scale(1.03)' : 'scale(1)', transition: 'transform 0.15s ease' }} />
                  <p style={{ fontSize: '1vw', fontWeight: 700, marginTop: '0.5vw', color: i === simIdx ? '#fff' : 'rgba(255,255,255,0.65)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ Écran Audio et sous-titres ═══════ */}
      {screen === 'audio' && (
        <div style={subScreenStyle} data-testid="tv-audio-screen">
          {subHeader}
          <div style={{ position: 'absolute', top: '22vh', left: '4vw', width: '26vw' }}>
            {audMenu.map((m, i) => {
              const fc = audZone === 'menu' && i === audMenuIdx;
              return (
                <div key={m.label} style={menuItemStyle(fc, i === audMenuIdx)} data-testid={`tv-audmenu-${i}`}
                  onClick={() => { setAudMenuIdx(i); setAudZone('list'); setAudIdx(0); }}>
                  <span>{m.label}</span>
                </div>
              );
            })}
            {content?.burned_subtitles_lang && (
              <p style={{ fontSize: '0.9vw', color: 'rgba(255,255,255,0.4)', marginTop: '1.2vw', paddingLeft: '1.6vw' }}>
                Sous-titres {content.burned_subtitles_lang.toLowerCase()} intégrés à l'image
              </p>
            )}
          </div>
          <div style={{ position: 'absolute', top: '22vh', left: '38vw', right: '4vw', bottom: '4vh', overflow: 'hidden' }}>
            <p style={{ fontSize: '1.15vw', fontWeight: 800, marginBottom: '1vw' }}>{audMenu[audMenuIdx]?.label}</p>
            {audOpts.map((o, i) => {
              const fc = audZone === 'list' && i === audIdx;
              const sel = audMenuIdx === 0 ? selAudio === o.id : selSub === o.id;
              return (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', padding: '0.7vw 1.4vw', marginBottom: '0.5vw', borderRadius: '2vw',
                  width: 'fit-content', minWidth: '20vw', fontSize: '1.1vw', fontWeight: 600, cursor: 'pointer',
                  background: fc ? '#fff' : 'transparent', color: fc ? '#0b0612' : 'rgba(255,255,255,0.85)' }}
                  data-testid={`tv-audopt-${i}`}
                  onClick={() => {
                    if (audMenuIdx === 0) { setSelAudio(o.id); try { sessionStorage.setItem('tv_pref_audio', o.id); } catch { /* ignore */ } }
                    else { setSelSub(o.id); try { sessionStorage.setItem('tv_pref_sub', o.id); } catch { /* ignore */ } }
                  }}>
                  <span style={{ width: '1.6vw', display: 'inline-flex' }}>{sel && <Check style={{ width: '1.15vw', height: '1.15vw' }} strokeWidth={3} />}</span>
                  {o.label}
                </div>
              );
            })}
            <p style={{ fontSize: '0.9vw', color: 'rgba(255,255,255,0.4)', marginTop: '1.4vw' }}>
              Votre choix sera appliqué au lancement de la lecture.
            </p>
          </div>
        </div>
      )}

      {descExpanded && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 30, background: 'rgba(5,2,10,0.96)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4vw 14vw' }} data-testid="tv-desc-overlay">
          <h2 style={{ fontSize: '1.9vw', fontWeight: 800, marginBottom: '1.4vw' }}>{content?.title}</h2>
          <p style={{ fontSize: '1.15vw', color: 'rgba(255,255,255,0.8)', lineHeight: 1.7, textAlign: 'left', maxHeight: '58vh', overflow: 'hidden' }}>
            {content?.description}
          </p>
          <div className="tv-btn tv-focused" onClick={() => setDescExpanded(false)}
            style={{ display: 'flex', alignItems: 'center', marginTop: '2.2vw', padding: '0.7vw 2.2vw',
              fontSize: '1.1vw', fontWeight: 700, borderRadius: '2vw', cursor: 'pointer', background: '#fff', color: '#000' }}
            data-testid="tv-desc-back-btn">
            ← Retour
          </div>
        </div>
      )}
      {listToast && (
        <div style={{ position: 'fixed', bottom: '6vh', left: '50%', transform: 'translateX(-50%)', zIndex: 60,
          background: 'rgba(11,6,18,0.94)', border: '0.12vw solid rgba(255,255,255,0.3)', borderRadius: '0.6vw',
          padding: '1vw 2.2vw', display: 'flex', alignItems: 'center', boxShadow: '0 0.4vw 2vw rgba(0,0,0,0.6)' }}
          className="tv-fade-in" data-testid="tv-list-toast">
          {listToast === 'added'
            ? <Check style={{ width: '1.6vw', height: '1.6vw', color: '#E9641A' }} />
            : <Plus style={{ width: '1.6vw', height: '1.6vw', color: 'rgba(255,255,255,0.7)', transform: 'rotate(45deg)' }} />}
          <span style={{ fontSize: '1.25vw', fontWeight: 700, marginLeft: '0.9vw' }}>
            {listToast === 'added' ? 'Ajouté à Ma liste' : 'Retiré de Ma liste'}
          </span>
        </div>
      )}
    </div>
  );
}

function fmtTime(sec) {
  if (!sec) return '';
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return h > 0 ? ` à ${h}h${String(m).padStart(2, '0')}` : ` à ${m} min`;
}
